import { RmaLgmCommodityCodes, RmaLgmTypeCodes } from '@harvestiq/constants';
import {
  zCoercedBoolean,
  zDecimalNullable,
  zDecimalRequired,
  zIsoDate,
} from '@harvestiq/zod';
import { z } from '@harvestiq/zod';
import { addLgmTargetSchemaValidation } from './helpers';
import { lgmSingleHeadQuoteSchema } from '@harvestiq/usda-common/lgm-helpers/lgm-single-head-quote';
import { lgmMarketingMonthOffsetSchema } from '@harvestiq/usda-common/lgm-helpers/lgm-marketing-month-offset';
import { futuresOptionsSchema } from '@harvestiq/external-clients-barchart-common/endpoints/get-futures-options';

const lgmVsCmeRequestSchemaObj = z.object({
  deductible: z.coerce.number(),
  commodityCode: z.nativeEnum(RmaLgmCommodityCodes, {
    message: 'Invalid commodity code',
  }),
  typeCode: z.nativeEnum(RmaLgmTypeCodes, {
    message: 'Invalid type code',
  }),
  salesEffectiveDate: zIsoDate(),
  isPre2026BeginningFarmerRancher: zCoercedBoolean().optional(),
  isVeteranFarmerRancher: zCoercedBoolean().optional(),
  beginningFarmerRancherCropYearCount: z.coerce
    .number()
    .int()
    .nullable()
    .optional(),
  liveCattleTargetCwtPerHead: zDecimalRequired().optional(),
  feederCattleTargetCwtPerHead: zDecimalRequired().optional(),
  cornTargetBuPerHead: zDecimalRequired().optional(),
  leanHogTargetCwtPerHead: zDecimalRequired().optional(),
  soybeanMealTargetTonPerHead: zDecimalRequired().optional(),
});

// /quoter/lgm-vs-cme
export const lgmVsCmeRequestSchema = addLgmTargetSchemaValidation({
  schema: lgmVsCmeRequestSchemaObj,
  lgmTypeCodeField: 'typeCode',
});

// Using different schema than above to avoid Zod issue with returning
// wrong type from refine see: https://github.com/colinhacks/zod/issues/2474
export type LgmVsCmeRequest = z.infer<typeof lgmVsCmeRequestSchemaObj>;

const underlyingOptionsDatum = z.object({
  adjustedSettlement: zDecimalRequired(),
  marketingMonthOffset: lgmMarketingMonthOffsetSchema,
  futuresOption: futuresOptionsSchema,
});

const settlementDataSchema = z
  .object({
    settlementWeightedAverage: zDecimalRequired(),
    underlyingOptionData: z.array(underlyingOptionsDatum),
  })
  .nullable();
export type SettlementData = z.infer<typeof settlementDataSchema>;

const lgmCMEDataSchema = z.object({
  corn: z.array(settlementDataSchema),
  liveCattle: z.array(settlementDataSchema).optional(),
  feederCattle: z.array(settlementDataSchema).optional(),
  leanHog: z.array(settlementDataSchema).optional(),
  soybeanMeal: z.array(settlementDataSchema).optional(),
  cmeEquivalent: z.array(zDecimalNullable()),
});
export type LgmCMEData = z.infer<typeof lgmCMEDataSchema>;

export const lgmVsCmeSuccessResponseSchema = z.object({
  lgmCMEData: lgmCMEDataSchema,
  lgmSingleHeadQuoteData: z.array(lgmSingleHeadQuoteSchema),
});
export type LgmVsCmeSuccessResponse = z.infer<
  typeof lgmVsCmeSuccessResponseSchema
>;

export const lgmVsCmeErrorResponseSchema = z.object({
  errors: z
    .array(
      z.object({
        message: z.string(),
      })
    )
    .or(z.string()),
});

export const lgmVsCmeResponseSchema = lgmVsCmeSuccessResponseSchema.or(
  lgmVsCmeErrorResponseSchema
);

export type LgmVsCmeResponse = z.infer<typeof lgmVsCmeResponseSchema>;
